/**
 * Timberland desktop multi-level nav drawer (header type 8).
 */
class TbDesktopNavDrawer {
  constructor(root) {
    this.root = root;
    this.overlay = root.querySelector('[data-tb-ddn-overlay]');
    this.panels = root.querySelector('[data-tb-ddn-panels]');
    this.primaryPanel = root.querySelector('[data-tb-ddn-primary]');
    this.closeEls = root.querySelectorAll('[data-tb-ddn-close]');
    this.subTriggers = root.querySelectorAll('[data-tb-ddn-open-sub]');
    this.subpanels = root.querySelectorAll('[data-tb-ddn-subpanel]');
    this.backButtons = root.querySelectorAll('[data-tb-ddn-back]');
    this.subCloseButtons = root.querySelectorAll('[data-tb-ddn-close-sub]');
    this._onDocKeydown = this._onDocKeydown.bind(this);
    this._onOverlayClick = this._onOverlayClick.bind(this);
    this._onAccClick = this._onAccClick.bind(this);
    this._onPanelsTransitionEnd = this._onPanelsTransitionEnd.bind(this);
    this._onSubpanelTransitionEnd = this._onSubpanelTransitionEnd.bind(this);
    this._scrollLockClass = 'tb-ddn-page--locked';
    /** @type {number | null} */
    this._pendingOpenRaf = null;
    /** @type {number | null} */
    this._closeFallbackTimer = null;
    /** @type {HTMLElement | null} */
    this._closeTransitionEl = null;
    this._closing = false;
    /** @type {HTMLElement | null} */
    this._subpanelClosingEl = null;
    /** @type {number | null} */
    this._subpanelCloseFallbackTimer = null;
    this._subpanelClosing = false;
    /** @type {(() => void) | null} */
    this._subSwapAnimCleanup = null;
    /** @type {number | null} */
    this._subSwapFallbackTimer = null;
    this._subSwapToken = 0;
  }

  init() {
    document.querySelectorAll('[data-tb-ddn-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.closeEls.forEach((el) => el.addEventListener('click', () => this.close()));

    if (this.overlay) {
      this.overlay.addEventListener('click', this._onOverlayClick);
    }

    this.subTriggers.forEach((btn) => {
      btn.addEventListener('click', () => this._openSubpanel(btn));
    });

    this.backButtons.forEach((btn) => {
      btn.addEventListener('click', () => this._closeSubpanel(false));
    });

    this.subCloseButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (window.matchMedia('(max-width: 1024px)').matches) {
          this.close();
        } else {
          this._closeSubpanel(false);
        }
      });
    });

    this.root.addEventListener('click', this._onAccClick);
    this._initAccPanelsA11y();
  }

  /** Grandchild accordions: closed panels start inert / aria-hidden for keyboard users. */
  _initAccPanelsA11y() {
    this.root.querySelectorAll('[data-tb-ddn-acc]').forEach((wrap) => {
      if (!(wrap instanceof HTMLElement)) return;
      const open = wrap.classList.contains('tb-ddn__acc--open');
      const panel = wrap.querySelector('[data-tb-ddn-acc-panel]');
      if (!(panel instanceof HTMLElement)) return;
      this._setAccPanelA11y(panel, open);
    });
  }

  /**
   * @param {HTMLElement} panel
   * @param {boolean} open
   */
  _setAccPanelA11y(panel, open) {
    panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (open) {
      panel.removeAttribute('inert');
    } else {
      panel.setAttribute('inert', '');
    }
  }

  _onOverlayClick() {
    this.close();
  }

  _onDocKeydown(e) {
    if (e.key === 'Escape') {
      this.close();
    }
  }

  _lockScroll() {
    document.documentElement.classList.add(this._scrollLockClass);
    document.body.classList.add(this._scrollLockClass);
  }

  _unlockScroll() {
    document.documentElement.classList.remove(this._scrollLockClass);
    document.body.classList.remove(this._scrollLockClass);
  }

  open() {
    if (this._closing) {
      this._clearCloseAnimation();
    }

    if (this.root.classList.contains('tb-ddn--open')) return;

    const wasHidden = this.root.hasAttribute('hidden');
    if (wasHidden) {
      this.root.removeAttribute('hidden');
      this.root.setAttribute('aria-hidden', 'false');
      this._lockScroll();
      document.addEventListener('keydown', this._onDocKeydown);
    }

    const applyOpenClass = () => {
      this.root.classList.add('tb-ddn--open');
      this._pendingOpenRaf = null;
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      applyOpenClass();
      return;
    }

    if (this._pendingOpenRaf !== null) {
      cancelAnimationFrame(this._pendingOpenRaf);
      this._pendingOpenRaf = null;
    }

    // After `display: none` ([hidden]), adding the open class in the same turn
    // skips transform transitions — paint one frame closed, then open.
    this._pendingOpenRaf = requestAnimationFrame(() => {
      void this.root.offsetHeight;
      if (this.primaryPanel) void this.primaryPanel.offsetHeight;
      this._pendingOpenRaf = requestAnimationFrame(() => {
        applyOpenClass();
      });
    });
  }

  _clearCloseAnimation() {
    const el = this._closeTransitionEl || this.panels;
    if (el) {
      el.removeEventListener('transitionend', this._onPanelsTransitionEnd);
    }
    this._closeTransitionEl = null;
    if (this._closeFallbackTimer !== null) {
      clearTimeout(this._closeFallbackTimer);
      this._closeFallbackTimer = null;
    }
    this._closing = false;
  }

  _onPanelsTransitionEnd(e) {
    const el = this._closeTransitionEl;
    if (!el || e.target !== el || e.propertyName !== 'transform') return;
    this._finishCloseAfterTransition();
  }

  /** @returns {HTMLElement | null} */
  _mainCloseTransitionTarget() {
    if (window.matchMedia('(max-width: 1024px)').matches && this.primaryPanel) {
      return this.primaryPanel;
    }
    return this.panels;
  }

  _finishCloseAfterTransition() {
    const el = this._closeTransitionEl || this.panels;
    if (el) {
      el.removeEventListener('transitionend', this._onPanelsTransitionEnd);
    }
    this._closeTransitionEl = null;
    if (this._closeFallbackTimer !== null) {
      clearTimeout(this._closeFallbackTimer);
      this._closeFallbackTimer = null;
    }

    if (!this._closing) return;
    this._closing = false;

    this.root.setAttribute('hidden', '');
    this._unlockScroll();
    document.removeEventListener('keydown', this._onDocKeydown);
  }

  _abortOpenState() {
    this._clearCloseAnimation();
    this.root.classList.remove('tb-ddn--open');
    this.root.setAttribute('aria-hidden', 'true');
    this.root.setAttribute('hidden', '');
    this._unlockScroll();
    document.removeEventListener('keydown', this._onDocKeydown);
  }

  close() {
    if (this._pendingOpenRaf !== null) {
      cancelAnimationFrame(this._pendingOpenRaf);
      this._pendingOpenRaf = null;
    }

    if (this.root.hasAttribute('hidden')) {
      return;
    }

    if (this._closing) {
      return;
    }

    if (!this.root.classList.contains('tb-ddn--open')) {
      this._closeSubpanel(true);
      this._abortOpenState();
      return;
    }

    this._closeSubpanel(true);
    this.root.setAttribute('aria-hidden', 'true');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.root.classList.remove('tb-ddn--open');
      this.root.setAttribute('hidden', '');
      this._unlockScroll();
      document.removeEventListener('keydown', this._onDocKeydown);
      return;
    }

    this._closing = true;
    this.root.classList.remove('tb-ddn--open');

    this._closeTransitionEl = this._mainCloseTransitionTarget();
    if (this._closeTransitionEl) {
      this._closeTransitionEl.addEventListener('transitionend', this._onPanelsTransitionEnd);
    }

    this._closeFallbackTimer = window.setTimeout(() => {
      this._finishCloseAfterTransition();
    }, 450);
  }

  _abortSubpanelCloseAnimation() {
    if (this._subpanelClosingEl) {
      this._subpanelClosingEl.removeEventListener('transitionend', this._onSubpanelTransitionEnd);
      this._subpanelClosingEl = null;
    }
    if (this._subpanelCloseFallbackTimer !== null) {
      clearTimeout(this._subpanelCloseFallbackTimer);
      this._subpanelCloseFallbackTimer = null;
    }
    this._subpanelClosing = false;
  }

  _onSubpanelTransitionEnd(e) {
    if (e.target !== this._subpanelClosingEl || e.propertyName !== 'transform') return;
    this._finishSubpanelCloseAfterTransition();
  }

  _finishSubpanelCloseAfterTransition() {
    if (!this._subpanelClosing) return;
    this._subpanelClosing = false;
    if (this._subpanelClosingEl) {
      this._subpanelClosingEl.removeEventListener('transitionend', this._onSubpanelTransitionEnd);
      this._subpanelClosingEl = null;
    }
    if (this._subpanelCloseFallbackTimer !== null) {
      clearTimeout(this._subpanelCloseFallbackTimer);
      this._subpanelCloseFallbackTimer = null;
    }
    this._abortSubpanelSwap();
    this.subpanels.forEach((p) => {
      p.classList.remove('is-active');
      p.setAttribute('aria-hidden', 'true');
    });
    this.subTriggers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
  }

  _abortSubpanelSwap() {
    this._subSwapToken += 1;
    if (this._subSwapAnimCleanup) {
      this._subSwapAnimCleanup();
      this._subSwapAnimCleanup = null;
    }
    if (this._subSwapFallbackTimer !== null) {
      clearTimeout(this._subSwapFallbackTimer);
      this._subSwapFallbackTimer = null;
    }
    this.subpanels.forEach((p) => {
      p.classList.remove('tb-ddn__panel--secondary-swap-out');
    });
    this.root.querySelectorAll('.tb-ddn__list--l2').forEach((ul) => {
      ul.classList.remove('tb-ddn__sub-l2--exit', 'tb-ddn__sub-l2--enter');
    });
  }

  /**
   * @param {HTMLElement} outgoing
   * @param {HTMLElement} incoming
   * @param {HTMLButtonElement} btn
   */
  _swapSubpanel(outgoing, incoming, btn) {
    const myToken = this._subSwapToken;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    outgoing.classList.add('tb-ddn__panel--secondary-swap-out');
    outgoing.classList.remove('is-active');
    outgoing.setAttribute('aria-hidden', 'true');

    incoming.classList.add('is-active');
    incoming.setAttribute('aria-hidden', 'false');

    const outUl = outgoing.querySelector('.tb-ddn__list--l2');
    const inUl = incoming.querySelector('.tb-ddn__list--l2');

    this.subTriggers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
    btn.setAttribute('aria-expanded', 'true');

    let swapFinished = false;
    const finish = () => {
      if (swapFinished) return;
      if (myToken !== this._subSwapToken) return;
      swapFinished = true;
      if (this._subSwapAnimCleanup) {
        this._subSwapAnimCleanup();
        this._subSwapAnimCleanup = null;
      }
      outgoing.classList.remove('tb-ddn__panel--secondary-swap-out');
      if (outUl) {
        outUl.classList.remove('tb-ddn__sub-l2--exit', 'tb-ddn__sub-l2--enter');
      }
      if (inUl) {
        inUl.classList.remove('tb-ddn__sub-l2--exit', 'tb-ddn__sub-l2--enter');
      }
      if (this._subSwapFallbackTimer !== null) {
        clearTimeout(this._subSwapFallbackTimer);
        this._subSwapFallbackTimer = null;
      }
    };

    if (reducedMotion) {
      finish();
      return;
    }

    if (outUl) outUl.classList.add('tb-ddn__sub-l2--exit');
    if (inUl) inUl.classList.add('tb-ddn__sub-l2--enter');

    const targets = [outUl, inUl].filter(Boolean);
    let remaining = targets.length;

    const onAnimEnd = (e) => {
      if (myToken !== this._subSwapToken) return;
      if (e.animationName !== 'tb-ddn-sub-l2-out' && e.animationName !== 'tb-ddn-sub-l2-in') return;
      if (!targets.includes(/** @type {Element} */ (e.target))) return;
      remaining -= 1;
      if (remaining <= 0) {
        finish();
      }
    };

    if (remaining === 0) {
      finish();
      return;
    }

    targets.forEach((el) => {
      el.addEventListener('animationend', onAnimEnd);
    });

    this._subSwapAnimCleanup = () => {
      targets.forEach((el) => {
        el.removeEventListener('animationend', onAnimEnd);
      });
      if (this._subSwapFallbackTimer !== null) {
        clearTimeout(this._subSwapFallbackTimer);
        this._subSwapFallbackTimer = null;
      }
    };

    this._subSwapFallbackTimer = window.setTimeout(finish, 400);
  }

  _openSubpanel(btn) {
    const id = btn.getAttribute('data-tb-ddn-sub-target');
    if (!id) return;
    const panel = document.getElementById(id);
    if (!panel || !this.root.contains(panel)) return;

    this._abortSubpanelCloseAnimation();

    const current = this.root.querySelector('[data-tb-ddn-subpanel].is-active');
    if (current === panel) return;

    const switching =
      this.root.classList.contains('tb-ddn--secondary-open') &&
      current &&
      current !== panel;

    if (switching) {
      this._abortSubpanelSwap();
      this._swapSubpanel(current, panel, btn);
      return;
    }

    this._abortSubpanelSwap();

    this.subpanels.forEach((p) => {
      p.classList.remove('is-active', 'tb-ddn__panel--secondary-swap-out');
      p.setAttribute('aria-hidden', 'true');
    });

    panel.classList.add('is-active');
    panel.setAttribute('aria-hidden', 'false');
    this.root.classList.add('tb-ddn--secondary-open');

    this.subTriggers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
    btn.setAttribute('aria-expanded', 'true');
  }

  /**
   * @param {boolean} [instant] When true, skip slide-out transition (e.g. whole drawer closing).
   */
  _closeSubpanel(instant = false) {
    if (this._subpanelClosing) {
      return;
    }

    this._abortSubpanelSwap();

    if (!this.root.classList.contains('tb-ddn--secondary-open')) {
      this._abortSubpanelCloseAnimation();
      this.subpanels.forEach((p) => {
        p.classList.remove('is-active');
        p.setAttribute('aria-hidden', 'true');
      });
      this.subTriggers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      return;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (instant || reducedMotion) {
      this._abortSubpanelCloseAnimation();
      this.root.classList.remove('tb-ddn--secondary-open');
      this.subpanels.forEach((p) => {
        p.classList.remove('is-active');
        p.setAttribute('aria-hidden', 'true');
      });
      this.subTriggers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      return;
    }

    const active = this.root.querySelector('[data-tb-ddn-subpanel].is-active');
    if (!active) {
      this.root.classList.remove('tb-ddn--secondary-open');
      this.subTriggers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      return;
    }

    this._subpanelClosing = true;
    this._subpanelClosingEl = active;
    this.root.classList.remove('tb-ddn--secondary-open');

    active.addEventListener('transitionend', this._onSubpanelTransitionEnd);
    this._subpanelCloseFallbackTimer = window.setTimeout(() => {
      this._finishSubpanelCloseAfterTransition();
    }, 400);
  }

  _onAccClick(e) {
    const trigger = e.target.closest('[data-tb-ddn-acc-trigger]');
    if (!trigger || !this.root.contains(trigger)) return;
    const wrap = trigger.closest('[data-tb-ddn-acc]');
    if (!wrap) return;
    e.preventDefault();
    const panel = wrap.querySelector('[data-tb-ddn-acc-panel]');
    const open = !wrap.classList.contains('tb-ddn__acc--open');
    wrap.classList.toggle('tb-ddn__acc--open', open);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel instanceof HTMLElement) {
      this._setAccPanelA11y(panel, open);
    }
  }
}

window.TbDesktopNavDrawer = TbDesktopNavDrawer;

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-tb-ddn-root]').forEach((root) => {
    const instance = new TbDesktopNavDrawer(root);
    instance.init();
  });
});
